import { JSX, useEffect, useState } from 'react';
import CreateOshaReportStepOne from '../../views/CreateOshaReportStepOne';
import { useFormik } from 'formik';
import { getResubmitToOshaReportSchema } from './schema';
import CreateOshaReportStepTwo from '../../views/CreateOshaReportStepTwo';
import CreateOshaReportStepThree from '../../views/CreateOshaReportStepThree';
import CreateOshaReportStepFour from '../../views/CreateOshaReportStepFour';
import { useSearchParams } from 'react-router';
import { useApiClients } from '../../client/api/useApiClient';
import { useQuery } from '@tanstack/react-query';
import {
  GetEstablishmentResponseDto,
  GetReportsForOshaResponse,
  InvestigationWithAffectedBodyPartsSentence,
} from '@EHSNavigator/backend';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import { hideBodyScroll, showBodyScroll } from '../../utils/bodyScroll';
import OSHANotAvailable from '../../components/OSHANotAvailable';
import { dateOshaValidation } from '../../utils/oshaDateValidation';

export interface ResubmitOshaReportFormValues {
  annualAverageNumberOfEmployees: string;
  annualAverageNumberOfEmployeesWorkingHours: string;
  reason: string;
}
export default function ResubmitToOshaReportPage() {
  const [searchParams] = useSearchParams();
  const year = searchParams.get('year') ?? '2024';
  const { OshaReportingApi, EstablishmentApi } = useApiClients();

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [state, setState] = useState<{
    excludedRow: string[];
  }>({
    excludedRow: [],
  });

  const { data, isLoading, isRefetching } = useQuery<
    GetReportsForOshaResponse,
    Error,
    any
  >({
    queryKey: ['get-reports-for-osha', Number(year)],
    queryFn: async () => {
      const response =
        await OshaReportingApi.oshaReportingControllerGetReportsForOsha({
          year: Number(year),
          includeBoth: true,
        });
      return response?.data;
    },
  });

  useEffect(() => {
    const flaggedInjuries =
      data?.reports?.filter(
        (node: InvestigationWithAffectedBodyPartsSentence) =>
          node?.submittedToOsha === false,
      ) || [];
    setState((prev) => ({
      ...prev,
      excludedRow: flaggedInjuries?.map(
        (node: InvestigationWithAffectedBodyPartsSentence) => node?.id,
      ),
    }));
  }, [data]);

  const filterOutReports = data?.reports?.filter(
    (node: InvestigationWithAffectedBodyPartsSentence) =>
      !state?.excludedRow?.includes(node?.id),
  );

  const createOshaReportHandler = useFormik<ResubmitOshaReportFormValues>({
    enableReinitialize: true,
    initialValues: {
      annualAverageNumberOfEmployees:
        data?.summary?.response?.annual_average_employees || '',
      annualAverageNumberOfEmployeesWorkingHours:
        data?.summary?.response?.total_hours_worked || '',
      reason: '',
    },
    validationSchema: getResubmitToOshaReportSchema(
      currentStep,
      filterOutReports,
    ),

    onSubmit: async (values) => {
      try {
        const res =
          await OshaReportingApi.oshaReportingControllerResubmitToOsha({
            reSubmitInvestigationsToOshaRequest: {
              annualAverageEmployees: Number(
                values?.annualAverageNumberOfEmployees,
              ),
              totalHoursWorked: Number(
                values?.annualAverageNumberOfEmployeesWorkingHours,
              ),
              year: 2024,
              investigationIds: filterOutReports?.map(
                (node: InvestigationWithAffectedBodyPartsSentence) => node?.id,
              ),
              reason: values?.reason,
            },
          });

        toast.success(res?.data?.message);
        setCurrentStep(4);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });

  const handleNextStep = async (steps: number) => {
    if (steps < currentStep) {
      setCurrentStep(steps);
      window.scrollTo(0, 0);
    } else {
      const errors = await createOshaReportHandler.validateForm();
      if (Object.keys(errors).length === 0) {
        window.scrollTo(0, 0);
        setCurrentStep(steps);
      } else {
        const touchedFields = Object.keys(errors).reduce((acc: any, key) => {
          acc[key] = true;
          return acc;
        }, {});

        createOshaReportHandler.setTouched(touchedFields);
      }
    }
  };

  function areObjectsEqual(obj1: any, obj2: any) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => obj1[key] === obj2[key]);
  }

  const isFormUnchanged = areObjectsEqual(
    {
      annualAverageNumberOfEmployees:
        createOshaReportHandler?.values?.annualAverageNumberOfEmployees,

      annualAverageNumberOfEmployeesWorkingHours:
        createOshaReportHandler?.values
          ?.annualAverageNumberOfEmployeesWorkingHours,
      reason: createOshaReportHandler?.values?.reason,
      injuriesSelected: JSON.stringify(state?.excludedRow),
    },
    {
      annualAverageNumberOfEmployees:
        data?.summary?.response?.annual_average_employees,
      annualAverageNumberOfEmployeesWorkingHours:
        data?.summary?.response?.total_hours_worked,
      reason: '',
      injuriesSelected: JSON.stringify(
        data?.reports
          ?.filter(
            (node: InvestigationWithAffectedBodyPartsSentence) =>
              node?.submittedToOsha === false,
          )
          ?.map(
            (node: InvestigationWithAffectedBodyPartsSentence) => node?.id,
          ) || [],
      ),
    },
  );

  const { data: eData } = useQuery<
    GetEstablishmentResponseDto['establishment'],
    Error,
    any
  >({
    queryKey: ['get-establishment'],
    queryFn: async () => {
      const response =
        await EstablishmentApi.establishmentControllerGetEstablishment();
      return response?.data?.establishment;
    },
  });

  const steps: Record<number, JSX.Element> = {
    1: (
      <CreateOshaReportStepOne
        handleExcludeRow={setState}
        excludedRow={state.excludedRow}
        handleSteps={handleNextStep}
        data={data}
        year={year}
      />
    ),
    2: (
      <CreateOshaReportStepTwo
        handleSteps={handleNextStep}
        data={filterOutReports}
      />
    ),
    3: (
      <CreateOshaReportStepThree
        handleSteps={handleNextStep}
        formHandler={createOshaReportHandler}
        isResubmit
        disbaleButtons={isFormUnchanged}
      />
    ),
    4: <CreateOshaReportStepFour establishment={eData} />,
  };

  const isDateAvailable = dateOshaValidation();

  useEffect(() => {
    const handleScroll = () => {
      if (!isDateAvailable) {
        hideBodyScroll();
      } else {
        showBodyScroll();
      }
    };

    handleScroll();
    return () => {
      showBodyScroll();
    };
  }, [isDateAvailable]);

  if (isLoading || isRefetching) {
    return <Loader />;
  }

  return (
    <div className="relative flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <form
        action=""
        className="flex h-full w-full flex-col"
        onSubmit={createOshaReportHandler.handleSubmit}
      >
        {steps[currentStep]}
      </form>
      {!isDateAvailable && <OSHANotAvailable />}
    </div>
  );
}
